import { useEffect, useRef, useState } from "react";
import { Eraser, Check } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Canvas-based signature pad.
 * Supports mouse + touch (Android). Exposes onSave(dataUrl, signerName).
 */
export default function SignaturePad({ onSave, saving }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [signer, setSigner] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Set backing store size to match displayed size with DPR for crisp lines.
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#111111";
      // White background so PDF looks clean
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#111111";
    hasInk.current = false;
  };

  const save = () => {
    if (!signer.trim()) { toast.error("Enter signer name"); return; }
    if (!hasInk.current) { toast.error("Please sign in the box"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl, signer.trim());
  };

  return (
    <div>
      <Label className="text-xs text-slate-600">Signer Name</Label>
      <Input data-testid="sig-signer-name" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Full name" />
      <div className="mt-3 relative border hairline" style={{ background: "#FFFFFF" }}>
        <canvas
          ref={canvasRef}
          data-testid="sig-canvas"
          className="w-full h-40 touch-none cursor-crosshair block"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest text-black/40 pointer-events-none font-mono">Sign above</span>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button className="btn-ghost text-sm flex items-center gap-1" onClick={clear} data-testid="sig-clear">
          <Eraser size={14} /> Clear
        </button>
        <button className="btn-primary text-sm flex items-center gap-1" onClick={save} disabled={saving} data-testid="sig-save">
          <Check size={14} /> {saving ? "Saving…" : "Save Signature"}
        </button>
      </div>
    </div>
  );
}
